import { useEffect, type FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { EventForm } from "@/components/events/EventForm";
import { EventImageUpload } from "@/components/events/EventImageUpload";
import { useEvent, useEventForm, useUpdateEvent } from "@/hooks/useEvents";
import { useAuth } from "@/hooks/useAuth";
import { CreateEventRequest } from "@/types";
import { useQueryClient } from "@tanstack/react-query";

export default function EventEditPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("events");
  const { user } = useAuth();
  const { data: event, isLoading, error } = useEvent(eventId ?? "");
  const { eventData, errors, validate, updateField } = useEventForm(event);
  const updateEventMutation = useUpdateEvent();

  useEffect(() => {
    if (!eventId) {
      navigate("/events");
    }
  }, [eventId, navigate]);

  if (!eventId) {
    return null;
  }

  if (isLoading) {
    return (
 <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
 <div className="animate-pulse space-y-6">
 <div className="h-8 bg-secondary-200 rounded w-1/3"></div>
 <div className="h-64 bg-secondary-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
 <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
 <div className="text-center">
        <p className="text-lg text-red-600 mb-4">{t("events:edit.failedToLoad")}</p>
          <button
            onClick={() => navigate("/events")}
            className="btn-primary px-4 py-2"
          >
            {t("events:edit.backToEvents")}
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await updateEventMutation.mutateAsync({
        id: event.id,
        eventData: eventData as CreateEventRequest,
      });
      navigate(`/events/${event.id}`);
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  const handleCancel = () => {
    navigate(`/events/${event.id}`);
  };

  return (
 <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
 <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(`/events/${event.id}`)}
 className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
        >
 <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="page-title">
            {t("events:edit.title")}
          </h1>
          <p className="page-subtitle">
            {t("events:edit.subtitle", { name: event.name })}
          </p>
        </div>
      </div>

      {/* Form */}
 <div className="space-y-6">
 <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <EventForm
            eventData={eventData}
            errors={errors}
            onUpdateField={updateField}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={updateEventMutation.isPending}
            isEditing={true}
            currentAccessLevel={user?.accessLevel}
          />
        </div>

        {/* Image Upload Section */}
 <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <EventImageUpload
            eventId={event.id}
            currentImageUrl={event.coverImageUrl}
            onImageUploaded={() => {
              // Invalidate and refetch the event data to get new presigned URL
              queryClient.invalidateQueries({ queryKey: ["event", event.id] });
            }}
            onImageDeleted={() => {
              // Invalidate and refetch the event data
              queryClient.invalidateQueries({ queryKey: ["event", event.id] });
            }}
          />
        </div>
      </div>
    </div>
  );
}
